import FileSaver from "file-saver";
import { compact, get, sortBy } from "lodash";
import moment from "moment";
import React from "react";
import * as Yup from "yup";
import { DOWNLOAD_VIA_BROWSER } from "../../config";
import {
    NestedOption,
    Option,
    allStatusOptions,
    deviceOptions,
    discoveryOptions,
    getJobCategoryOptions,
    getKanbanStatusOptions,
    germanLvlOptions,
    shirtSizeOptions
} from "../../constants/index";
import { Locales } from "../../i18n/Locales";
import { MessageIDS, t } from "../../i18n/util";
import {
    DeviceEnum,
    GetApplicantDetailsResponseHistory,
    GetApplicantDetailsResponseGermanLvlEnum,
    PatchApplicantDetailsResponseLanguageEnum
} from "../../network/APITypes";
import { THEME, isDal } from "../../theme/ThemeStore";
import {
    DiscoveryEnum,
    IApplicationStateEnum,
    IGetApplicantDetailsResponse,
    JobCategoryEnum,
    TemplateEnum
} from "../../types/models";
import { FileError } from "../application/DataUploadForm";
import { MAX_EXPERIENCE } from "../application/DetailsGrid";
import { ApplicantPicture, NoApplicantPicture } from "../ui/Primitives";
import { generalStore } from "../../stores/GeneralStore";

export const transitionToTemplate: (
    fromApplicationState?: IApplicationStateEnum,
    toApplicationState?: IApplicationStateEnum,
    jobCategory?: string,
    online?: boolean
) => TemplateEnum = (
    fromApplicationState?: IApplicationStateEnum,
    toApplicationState?: IApplicationStateEnum,
    jobCategory?: string,
    online?: boolean
) => {
        switch (toApplicationState) {
            case "coding_example":
                switch (jobCategory) {
                    case "ios":
                        return "codeExample_information_ios";
                    case "android":
                        return "codeExample_information_android";
                    case "web":
                        return "codeExample_information_frontend_regular";
                    case "backend":
                        return "codeExample_information_backend";
                    case "flutter":
                        return "codeExample_information_flutter";
                    default:
                        return "codeExample_information_frontend_junior";
                }
            case "first_interview":
                switch (fromApplicationState) {
                    case "application_received":
                        if (online) {
                            return "firstInterview_online";
                        } else {
                            return "firstInterview";
                        }
                    case "coding_example":
                        if (online) {
                            return "firstInterview_codeExample_online";
                        } else {
                            return "firstInterview_codeExample";
                        }
                    default:
                        if (online) {
                            return "firstInterview_online";
                        } else {
                            return "firstInterview";
                        }
                }
            case "second_interview":
                if (online) {
                    return "secondInterview_online";
                } else {
                    return "secondInterview";
                }
            case "denied":
                if (fromApplicationState === "coding_example") {
                    return "codeExample_rejection_general";
                } else if (fromApplicationState === "first_interview") {
                    return "rejection_first_interview";
                } else {
                    if (jobCategory === "apprentice") {
                        return "rejection_apprenticeship";
                    } else if (jobCategory === "dal_apprentice") {
                        return "rejection_internship";
                    } else {
                        return "rejection_general";
                    }
                }
            case "evidence":
                if (fromApplicationState === "coding_example") {
                    return "codeExample_rejection_evidence";
                } else if (fromApplicationState === "first_interview") {
                    return "rejection_first_interview_evidence";
                } else if (jobCategory === "dal_apprentice") {
                    return "rejection_internship_evidence";
                } else {
                    return "rejection_evidence";
                }
            case "hired":
                return "hired";
            default:
                return "rejection_general";
        }
    };

export const templateToString = (
    language: PatchApplicantDetailsResponseLanguageEnum,
    templateLabel: TemplateEnum,
    templateData: {
        firstName: string;
        userName: string;
    }
) => {
    let stringKey: MessageIDS | undefined;

    if (THEME.THEME_NAME === "aaa") {
        const mapping: Record<string, MessageIDS> = {
            rejection_general: "template.rejection.general.aaa.de",
            rejection_evidence: "template.rejection.evidence.aaa.de",
            rejection_abroad: "template.rejection.abroad.de",
            rejection_study: "template.rejection.study.de",
            rejection_apprenticeship: "template.rejection.apprenticeship.de",
            rejection_internship: "template.rejection.internship.aaa.de",
            rejection_internship_evidence: "template.rejection.internship.evidence.aaa.de",
            codeExample_rejection_evidence: "template.codeExample.rejection.evidence.de",
            codeExample_rejection_feedback: "template.codeExample.rejection.feedback.de",
            codeExample_rejection_general: "template.codeExample.rejection.general.de",
            rejection_first_interview: "template.rejection.firstInterview.aaa.de",
            rejection_first_interview_evidence: "template.rejection.firstInterview.evidence.aaa.de",
            codeExample_information_android: "template.codeExample.information.android.de",
            codeExample_information_ios: "template.codeExample.information.ios.de",
            codeExample_information_frontend_junior: "template.codeExample.information.frontend.junior.de",
            codeExample_information_frontend_regular: "template.codeExample.information.frontend.regular.de",
            codeExample_information_backend: "template.codeExample.information.backend.de",
            codeExample_information_flutter: "template.codeExample.information.flutter.de",
            codeExample_information_allteams: "template.codeExample.information.allteams.de",
            firstInterview: "template.firstInterview.application.aaa.de",
            firstInterview_online: "template.firstInterview.application.online.aaa.de",
            firstInterview_codeExample: "template.firstInterview.codeExample.de",
            firstInterview_codeExample_online: "template.firstInterview.codeExample.online.de",
            secondInterview: "template.secondInterview.invitation.aaa.de",
            secondInterview_online: "template.secondInterview.invitation.online.aaa.de",
            hired: "template.hired.aaa.de"
        };

        stringKey = mapping[templateLabel];
    } else if (THEME.THEME_NAME === "dal") {
        const mapping: Record<string, MessageIDS> = {
            rejection_general: "template.rejection.general.dal.de",
            rejection_evidence: "template.rejection.evidence.dal.de",
            rejection_abroad: "template.rejection.abroad.de",
            rejection_study: "template.rejection.study.de",
            rejection_apprenticeship: "template.rejection.apprenticeship.de",
            rejection_internship: "template.rejection.internship.dal.de",
            rejection_internship_evidence: "template.rejection.internship.evidence.dal.de",
            rejection_first_interview: "template.rejection.firstInterview.dal.de",
            rejection_first_interview_evidence: "template.rejection.firstInterview.evidence.dal.de",
            firstInterview: "template.firstInterview.application.dal.de",
            firstInterview_online: "template.firstInterview.application.online.dal.de",
            secondInterview: "template.secondInterview.invitation.dal.de",
            secondInterview_online: "template.secondInterview.invitation.online.dal.de",
            hired: "template.hired.dal.de"
        };

        stringKey = mapping[templateLabel];
    }

    if (stringKey) {
        if (language !== "de") {
            // Replace .de with .en
            stringKey = `${stringKey.slice(0, -3)}.${language}` as MessageIDS;
        }
        return t(stringKey, templateData);
    }
};

export type CodingIdParam = {
    codingId: string;
};

export type ApplicantIdParam = {
    applicantId: string;
};

export const getFilenameFromPath = (path: string) => {
    if (path.indexOf("\\") >= 0) {
        const elements = path.split("\\");
        return elements[elements.length - 1];
    }

    if (path.indexOf("/") >= 0) {
        const elements = path.split("/");
        return elements[elements.length - 1];
    }

    return path;
};

export const downloadSecureLink = async (url?: string, fileName?: string) => {
    if (!url || !fileName) {
        return;
    }

    if (!DOWNLOAD_VIA_BROWSER) {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            FileSaver.saveAs(blob, getFilenameFromPath(fileName));
        } catch (error) {
            generalStore.setError(t("error.download"));
        }
    } else {
        window.open(url, "_self");
        // Need this because calling too many window.open at once freaks out some browsers resulting
        // in skipped downloads
        return new Promise<void>((resolve, reject) => {
            setTimeout(() => resolve(), 1000);
        });
    }
};

export const formatISODateTime = (date?: Date | moment.Moment) => {
    if (!date) {
        return "";
    }

    return moment(date).format("YYYY-MM-DD HH:mm:ss");
};

export const formatISODateOnly = (date?: Date | moment.Moment | null) => {
    if (!date) {
        return undefined;
    }

    return moment(date).format("YYYY-MM-DD");
};

export const formatISOTimeOnly = (date?: Date | moment.Moment) => {
    if (!date) {
        return "";
    }

    return moment(date).format("HH:mm");
};

export const localeToFormat = (locale: Locales) => {
    switch (locale) {
        case "de":
            return "de-DE";
        case "en":
            return "en-EN";
        default:
            break;
    }
};

export const fileErrorToString = (fieldKey: string, fileError: FileError, maxSize?: number) => {
    let fileName = "";
    let fileErrorMessage = "";

    switch (fieldKey) {
        case "resume":
            fileName = t("screen.dataUpload.resume");
            break;
        case "photo":
            fileName = t("screen.dataUpload.photo");
            break;
        case "portfolio":
            fileName = t("screen.dataUpload.input.portfolio");
            break;
        case "video":
            fileName = t("screen.dataUpload.video");
            break;
        case "motivationalLetter":
            fileName = t("screen.dataUpload.motivationalLetter");
            break;
        default:
            break;
    }

    switch (fileError) {
        case "type":
            if (
                fieldKey === "resume" ||
                fieldKey === "portfolio" ||
                fieldKey === "motivationalLetter" ||
                fieldKey === "optionalFile"
            ) {
                fileErrorMessage = t("error.fileTypePdf");
            } else if (fieldKey === "photo") {
                fileErrorMessage = t("error.fileTypeJpg");
            } else if (fieldKey === "video") {
                fileErrorMessage = t("error.fileTypeMp4");
            } else if (fieldKey === "solution") {
                fileErrorMessage = t("error.fileTypeZip");
            }
            break;
        case "size":
            if (maxSize) {
                fileErrorMessage = t("error.size", { size: maxSize });
            } else if (fieldKey === "video") {
                fileErrorMessage = t("error.size95Mb");
            } else {
                fileErrorMessage = t("error.size5Mb");
            }
            break;
        case "amount":
            fileErrorMessage = t("error.amount");
            break;
        case "required":
            if (fieldKey === "resume") {
                return t("error.missingCV");
            } else {
                return t("error.missingFile");
            }
        default:
            if (fileError !== null) {
                return fileError;
            }
            break;
    }

    if (fieldKey !== "optionalFile" && fieldKey !== "solution") {
        return `${fileName}: ${fileErrorMessage}`;
    } else {
        return fileErrorMessage;
    }
};

export const getSortedTranslatedOptions = (options: Option<any>[]) =>
    sortBy(
        options.map((option) => ({
            label: t(option.messageId),
            value: option.value
        })),
        (option) => option.label.toLowerCase()
    );

export const getTranslatedOptions = (options: Option<any>[]) =>
    options.map((option) => ({
        label: t(option.messageId),
        value: option.value
    }));

export const optionToString = (options: Option<any>[], option?: string | null) => {
    const messageID = options.find((item) => item.value === option)?.messageId;

    if (messageID) {
        return t(messageID);
    } else {
        return "";
    }
};

export const nestedOptionsToString = (options: Array<NestedOption<any> | Option<any>>, option: string) => {
    let messageID: MessageIDS | undefined = undefined;

    for (const currentOption of options) {
        if (get(currentOption, "value") === option) {
            messageID = currentOption.messageId;
            break;
        }
        if (get(currentOption, "options")) {
            for (const subOption of get(currentOption, "options")) {
                if (subOption.value === option) {
                    messageID = subOption.messageId;
                    break;
                }
            }
        }
    }

    if (messageID) {
        return t(messageID);
    } else {
        return "";
    }
};

export const historyToString = (history: GetApplicantDetailsResponseHistory, name: string) => {
    if (history.additionalData === "Bewerbung eingegangen") {
        return `${name} ${t("screen.applicantHistory.sentInApplication")}`;
    } else {
        if (history.toApplicationState === "denied" && history.evidence) {
            return `${history.createdBy?.firstName} ${history.createdBy?.lastName} ${t(
                "screen.applicantHistory.changedFrom"
            )} ${optionToString(getKanbanStatusOptions(), history.fromApplicationState)} ${t(
                "screen.applicantHistory.to"
            )} ${optionToString(allStatusOptions, "evidence")}.`;
        } else if (history.fromApplicationState === "denied" && history.evidence) {
            return `${history.createdBy?.firstName} ${history.createdBy?.lastName} ${t(
                "screen.applicantHistory.changedFrom"
            )} ${optionToString(allStatusOptions, "evidence")} ${t("screen.applicantHistory.to")} ${optionToString(
                getKanbanStatusOptions(),
                history.toApplicationState
            )}.`;
        } else if (history.additionalData === "submitted coding example") {
            return `${name} ${t("screen.applicantHistory.submittedCodingSubmission")}`;
        } else {
            return `${history.createdBy?.firstName} ${history.createdBy?.lastName} ${t(
                "screen.applicantHistory.changedFrom"
            )} ${optionToString(getKanbanStatusOptions(), history.fromApplicationState)} ${t(
                "screen.applicantHistory.to"
            )} ${optionToString(getKanbanStatusOptions(), history.toApplicationState)}.`;
        }
    }
};

export const historyToApplicantPicture: (
    history: GetApplicantDetailsResponseHistory,
    applicant: IGetApplicantDetailsResponse
) => React.ReactNode = (history: GetApplicantDetailsResponseHistory, applicant: IGetApplicantDetailsResponse) => {
    if (history.additionalData === "Bewerbung eingegangen" || history.additionalData === "submitted coding example") {
        if (applicant.photo) {
            return (
                <ApplicantPicture
                    style={{
                        backgroundImage: `url(${applicant.photo.url})`
                    }}
                />
            );
        } else {
            return (
                <NoApplicantPicture>{`${applicant.firstName[0] ?? ""}${applicant.lastName[0] ?? ""}${applicant.lastName[1] ?? ""
                    }`}</NoApplicantPicture>
            );
        }
    } else {
        return (
            <ApplicantPicture
                style={{
                    backgroundImage: `url(${history.createdBy?.pictureUrl})`
                }}
            />
        );
    }
};

export const jobCategoryToColors = (jobCategory: JobCategoryEnum) => {
    let colors: { primary: string | undefined; shade1: string | undefined; shade2: string | undefined } = {
        primary: undefined,
        shade1: undefined,
        shade2: undefined
    };
    switch (jobCategory) {
        case "project_management":
            colors = {
                primary: THEME.customColors.pmPrimary,
                shade1: THEME.customColors.pmShade1,
                shade2: THEME.customColors.pmShade2
            };
            break;
        case "web":
            colors = {
                primary: THEME.customColors.webPrimary,
                shade1: THEME.customColors.webShade1,
                shade2: THEME.customColors.webShade2
            };
            break;
        case "backend":
            colors = {
                primary: THEME.customColors.webPrimary,
                shade1: THEME.customColors.webShade1,
                shade2: THEME.customColors.webShade2
            };
            break;
        case "qa":
            colors = {
                primary: THEME.customColors.qaPrimary,
                shade1: THEME.customColors.qaShade1,
                shade2: THEME.customColors.qaShade2
            };
            break;
        case "ios":
            colors = {
                primary: THEME.customColors.iOSPrimary,
                shade1: THEME.customColors.iOSShade1,
                shade2: THEME.customColors.iOSShade2
            };
            break;
        case "android":
            colors = {
                primary: THEME.customColors.androidPrimary,
                shade1: THEME.customColors.androidShade1,
                shade2: THEME.customColors.androidShade2
            };
            break;
        case "trainee":
            colors = {
                primary: THEME.customColors.trainingPrimary,
                shade1: THEME.customColors.trainingShade1,
                shade2: THEME.customColors.trainingShade2
            };
            break;
        case "apprentice":
            colors = {
                primary: THEME.customColors.trainingPrimary,
                shade1: THEME.customColors.trainingShade1,
                shade2: THEME.customColors.trainingShade2
            };
            break;
        case "dual_study":
            colors = {
                primary: THEME.customColors.trainingPrimary,
                shade1: THEME.customColors.trainingShade1,
                shade2: THEME.customColors.trainingShade2
            };
            break;
        case "flutter":
            colors = {
                primary: THEME.customColors.flutterPrimary,
                shade1: THEME.customColors.flutterShade1,
                shade2: THEME.customColors.flutterShade2
            };
            break;
        case "unsolicited":
            colors = {
                primary: THEME.customColors.unsolicitedPrimary,
                shade1: THEME.customColors.unsolicitedShade1,
                shade2: THEME.customColors.unsolicitedShade2
            };
            break;
        case "graphic":
            colors = {
                primary: THEME.customColors.graphicPrimary,
                shade1: THEME.customColors.graphicShade1,
                shade2: THEME.customColors.graphicShade2
            };
            break;
        case "uiux":
            colors = {
                primary: THEME.customColors.uiuxPrimary,
                shade1: THEME.customColors.uiuxShade1,
                shade2: THEME.customColors.uiuxShade2
            };
            break;
        case "motion":
            colors = {
                primary: THEME.customColors.motionPrimary,
                shade1: THEME.customColors.motionShade1,
                shade2: THEME.customColors.motionShade2
            };
            break;
        case "operations":
            colors = {
                primary: THEME.customColors.operationsPrimary,
                shade1: THEME.customColors.operationsShade1,
                shade2: THEME.customColors.operationsShade2
            };
            break;
        case "dal_frontend":
            colors = {
                primary: THEME.customColors.operationsPrimary,
                shade1: THEME.customColors.operationsShade1,
                shade2: THEME.customColors.operationsShade2
            };
            break;
        case "dal_full_stack":
            colors = {
                primary: THEME.customColors.fullStackPrimary,
                shade1: THEME.customColors.fullStackShade1,
                shade2: THEME.customColors.fullStackShade2
            };
            break;
        case "dal_backend":
            colors = {
                primary: THEME.customColors.backendPrimary,
                shade1: THEME.customColors.backendShade1,
                shade2: THEME.customColors.backendShade2
            };
            break;
        case "dal_ui_ux":
            colors = {
                primary: THEME.customColors.uiuxPrimary,
                shade1: THEME.customColors.uiuxShade1,
                shade2: THEME.customColors.uiuxShade2
            };
            break;
        case "dal_brand":
            colors = {
                primary: THEME.customColors.graphicPrimary,
                shade1: THEME.customColors.graphicShade1,
                shade2: THEME.customColors.graphicShade2
            };
            break;
        case "dal_motion_design":
            colors = {
                primary: THEME.customColors.motionPrimary,
                shade1: THEME.customColors.motionShade1,
                shade2: THEME.customColors.motionShade2
            };
            break;
        case "dal_project_management":
            colors = {
                primary: THEME.customColors.pmPrimary,
                shade1: THEME.customColors.pmShade1,
                shade2: THEME.customColors.pmShade2
            };
            break;
        case "dal_content_creation":
            colors = {
                primary: THEME.customColors.contentCreationPrimary,
                shade1: THEME.customColors.contentCreationShade1,
                shade2: THEME.customColors.contentCreationShade2
            };
            break;
        case "dal_unsolicited":
            colors = {
                primary: THEME.customColors.unsolicitedPrimary,
                shade1: THEME.customColors.unsolicitedShade1,
                shade2: THEME.customColors.unsolicitedShade2
            };
            break;
        case "dal_apprentice":
            colors = {
                primary: THEME.customColors.apprenticePrimary,
                shade1: THEME.customColors.apprenticeShade1,
                shade2: THEME.customColors.apprenticeShade2
            };
            break;
        // HRB-499: webdev was removed, creative director added -> reuse colors
        case "dal_creative_director":
            colors = {
                primary: THEME.customColors.webPrimary,
                shade1: THEME.customColors.webShade1,
                shade2: THEME.customColors.webShade2
            };
            break;
        default:
            colors = {
                primary: THEME.customColors.unsolicitedPrimary,
                shade1: THEME.customColors.unsolicitedShade1,
                shade2: THEME.customColors.unsolicitedShade2
            };
            break;
    }

    return colors;
};

export const statusOptionToPossibleDestinations = (statusOption: IApplicationStateEnum) => {
    // HRB-464: DAL has no "coding example" status
    const availableOptions = isDal()
        ? allStatusOptions.filter((option) => option.value !== "coding_example")
        : allStatusOptions;

    switch (statusOption) {
        case "hired":
            return [];
        case "evidence": {
            return availableOptions.filter((statusOption) => statusOption.value === "application_received");
        }
        default:
            return availableOptions.slice(availableOptions.findIndex((option) => option.value === statusOption) + 1);
    }
};

// IMPORTANT!

// Firefox allows users to input letters and other symbols into html "number" input fields.
// Chromium allows users to input the letter "e".
// As a workaround this project uses the function below to prevent users from entering such symbols.
// When specifying a "number" input field follow these steps:

// 1. Do NOT specity type = "number", this messes up the way values are handled.
//    In an "number" input field an invalid input is handled the same way as a nonexisting input.
//    This causes big troubles with the regex (see below)

// 2. Instead define the following function on the page where the "number" input fiels are used:
//      setInputFilter(Array.from(document.getElementsByClassName("intTextBox")), function (value) {
//        return /^\+?\d*$/.test(value);
//      });
//    Note: The regex allows for a + at the beginning and any number of following integers.
//          If however the field is defined as a "number" input field, any invalid input would be handled the same as an empty input.
//          Since empty inputs are valid, invalid inputs are thus also valid. This essentially makes the regex useless

// 3. In order for the input fields to be recognized by the function you need to specify the className in the input props like so:
//      inputProps={{ className: "intTextBox" }}

// https://stackoverflow.com/questions/469357/html-text-input-allow-only-numeric-input?utm_medium=organic&utm_source=google_rich_qa&utm_campaign=google_rich_qa

export const createInputFilter = (textbox: Element | null, inputFilter: (value: string) => boolean): void => {
    if (textbox === null) {
        return;
    }
    ["input", "keydown", "keyup", "mousedown", "mouseup", "select", "contextmenu", "drop"].forEach((event) => {
        textbox.addEventListener(event, function (
            this: (HTMLInputElement | HTMLTextAreaElement) & {
                oldValue: string;
                oldSelectionStart: number | null;
                oldSelectionEnd: number | null;
            }
        ) {
            if (inputFilter(this.value)) {
                this.oldValue = this.value;
                this.oldSelectionStart = this.selectionStart;
                this.oldSelectionEnd = this.selectionEnd;
            } else if (Object.prototype.hasOwnProperty.call(this, "oldValue")) {
                this.value = this.oldValue;
                if (this.oldSelectionStart !== null && this.oldSelectionEnd !== null) {
                    this.setSelectionRange(this.oldSelectionStart, this.oldSelectionEnd);
                }
            } else {
                this.value = "";
            }
        });
    });
};

export const getDataEntries = (
    applicant: {
        firstName: string;
        lastName: string;
        jobCategory: string | null;
        phone?: string;
        email: string;
        previousProjects?: string;
        discovery: DiscoveryEnum;
        customDiscoverySource?: string;
        salary: number | "";
        experience?: number;
        startDate: Date | null;
        noticePeriod?: number | "";
        device: DeviceEnum;
        germanLvl?: GetApplicantDetailsResponseGermanLvlEnum;
        workHours?: number | string | "";
        personnelNumber?: string;
        shirtSize?: string;
        deviceSN?: string;
        city: string;
    },
    type: "applicantDetails" | "verification"
) => {
    const isDal = THEME.THEME_NAME === "dal";

    const personalDataEntries: [string, string][] = [
        [t("screen.verify.personalData.name"), `${applicant.firstName} ${applicant.lastName}`],
        [t("screen.verify.personalData.jobCategory"), optionToString(getJobCategoryOptions(), applicant.jobCategory) || applicant.jobCategory || ""],
        [t("screen.verify.personalData.phone"), applicant.phone ?? ""],
        [t("screen.verify.personalData.email"), applicant.email],
        [t("screen.verify.personalData.website"), applicant.previousProjects ?? " ‎"],
        [
            t("screen.verify.personalData.discovery"),
            applicant.discovery === "different_source"
                ? applicant.customDiscoverySource ?? ""
                : optionToString(discoveryOptions, applicant.discovery)
        ]
    ];
    const detailsEntries: [string, string | undefined | number][] = compact([
        [t("screen.verify.details.salary"), `€ ${applicant.salary}`],
        [
            t("screen.verify.details.experience"),
            applicant.experience && applicant.experience > MAX_EXPERIENCE
                ? `${MAX_EXPERIENCE}+`
                : applicant.experience ?? 0
        ],
        [t("screen.verify.details.startDate"), moment(applicant.startDate).format("DD.MM.YYYY")],
        [t("screen.details.noticePeriod"), applicant.noticePeriod ?? 0],
        isDal ? null : [t("screen.verify.details.device"), optionToString(deviceOptions, applicant.device)],
        isDal ? null : [t("screen.verify.details.language"), optionToString(germanLvlOptions, applicant.germanLvl)],
        [t("screen.verify.details.workHours"), applicant.workHours],
        isDal ? null : [t("screen.verify.details.city"), applicant.city]
    ]);

    if (type === "applicantDetails" && applicant) {
        personalDataEntries.push([t("screen.applicantDetails.personnelNumber"), applicant.personnelNumber ?? ""]);
        personalDataEntries.push([
            t("screen.applicantDetails.shirtSize"),
            optionToString(shirtSizeOptions, applicant.shirtSize)
        ]);
        detailsEntries.push([t("screen.applicantDetails.deviceSN"), applicant.deviceSN]);
    }

    return { personalDataEntries, detailsEntries };
};

export const mimeTypesToFileInfo = (
    type: "application/pdf" | "image/jpeg" | "video/mp4" | "application/zip" | "application/x-zip-compressed",
    size?: number
) => {
    let dataType;
    let defaultSize;
    switch (type) {
        case "application/pdf":
            dataType = t("dataType.pdf");
            defaultSize = 5;
            break;
        case "application/zip":
        case "application/x-zip-compressed":
            dataType = t("dataType.zip");
            defaultSize = 50;
            break;
        case "image/jpeg":
            dataType = t("dataType.jpg");
            defaultSize = 5;
            break;
        case "video/mp4":
            dataType = t("dataType.mp4");
            defaultSize = 20;
            break;
    }
    return t("screen.dataUpload.fileInfo", { format: dataType, size: size ?? defaultSize });
};

export const megabytes = (megabytes: number) => {
    return megabytes * 1048576;
};

export const fromMegabytes = (bytes: number) => {
    return bytes / 1048576;
};

export function YupLocalizedNumber() {
    return Yup.number()
        .transform((_, value) => {
            if (typeof value === "number") {
                return value;
            }

            return Number(value.replace(",", "."));
        })
        .typeError(t("error.number"));
}

// Build full name from firstName, lastName
export const getFullName = <T extends { firstName: string; lastName: string }>(entity?: T | null) =>
    entity ? `${entity.firstName} ${entity.lastName}` : "";

export const getFormattedTimeRange = (startTime?: Date, endTime?: Date) => {
    if (!startTime || !endTime) {
        return;
    }

    return `${moment(startTime).format(t("screen.dateTimeFormat"))} - ${moment(endTime).format(
        t("screen.timeFormat")
    )}`;
};
